import type { Labels, SubdepartmentWithFamilies } from '@/types/treeViewTypes'
import { VpCheckbox, VpLoader } from '@vtmn-play/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { ANALYTICS_EVENTS, ANALYTICS_JOURNEY_CHANNELS, trackAnalyticsEvent } from '@/services/analytics'
import { FamiliesFooter } from './FamiliesFooter'
import { FamiliesModal } from './FamiliesModal'
import { FamilyCard } from './FamilyCard'
import { updateFamilies } from './utils'
import './families.scss'

export interface ChipFamilyType {
  id: number
  labels: Labels
  subdepartmentId: number
  subdepartmentLabels: Labels
}

interface FamiliesProps {
  subDepWithFamilies: SubdepartmentWithFamilies | null
  isLoading: boolean
  innerRef?: React.RefObject<HTMLElement | null>
}

export function Families({
  subDepWithFamilies,
  isLoading,
  innerRef,
}: Readonly<FamiliesProps>) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { season } = useParams<{ season: string }>()

  const [openModal, setOpenModal] = useState(false)
  const [selectedFamilies, setSelectedFamilies] = useState<ChipFamilyType[]>(
    [],
  )

  const familiesCount = subDepWithFamilies?.families.length || 0

  const toggleFamily = (family: ChipFamilyType) => {
    const action = selectedFamilies.some(f => f.id === family.id) ? 'remove' : 'add'
    trackAnalyticsEvent(ANALYTICS_EVENTS.NAV_FAMILY_TOGGLED, {
      season,
      journey_channel: ANALYTICS_JOURNEY_CHANNELS.HIERARCHY_NAVIGATION,
      universe_id: subDepWithFamilies?.universe.id,
      department_id: subDepWithFamilies?.department.id,
      subdepartment_id: subDepWithFamilies?.id,
      family_id: family.id,
      action,
    })
    setSelectedFamilies(prev => updateFamilies(prev, family))
  }

  const isFamiliesAllSelected = useMemo(() => {
    const familiesFromThisDep = selectedFamilies.filter(
      family => family.subdepartmentId === subDepWithFamilies?.id,
    )
    return familiesFromThisDep.length === subDepWithFamilies?.families.length
  }, [selectedFamilies, subDepWithFamilies])

  const checked = useMemo(() => {
    if (isFamiliesAllSelected)
      return true
    if (selectedFamilies.length > 0)
      return 'indeterminate'
    return false
  }, [isFamiliesAllSelected, selectedFamilies])

  const toggleAllFamilies = () => {
    if (subDepWithFamilies) {
      const remainingFamilies = selectedFamilies.filter(
        family => family.subdepartmentId !== subDepWithFamilies?.id,
      )
      trackAnalyticsEvent(ANALYTICS_EVENTS.NAV_FAMILIES_SELECT_ALL_TOGGLED, {
        season,
        journey_channel: ANALYTICS_JOURNEY_CHANNELS.HIERARCHY_NAVIGATION,
        universe_id: subDepWithFamilies.universe.id,
        department_id: subDepWithFamilies.department.id,
        subdepartment_id: subDepWithFamilies.id,
        families_count: subDepWithFamilies.families.length,
        action: isFamiliesAllSelected ? 'deselect_all' : 'select_all',
      })
      if (isFamiliesAllSelected) {
        setSelectedFamilies(remainingFamilies)
      }
      else {
        const newSelectionToAdd = subDepWithFamilies.families.map(
          family =>
            ({
              ...family,
              subdepartmentId: subDepWithFamilies.id,
              subdepartmentLabels: subDepWithFamilies.labels,
            }) as ChipFamilyType,
        )
        setSelectedFamilies([...remainingFamilies, ...newSelectionToAdd])
      }
    }
  }

  const getSubdepPath = () => {
    const sameSubdep = selectedFamilies.every(
      family => family.subdepartmentId === subDepWithFamilies?.id,
    )
    if (sameSubdep && isFamiliesAllSelected) {
      return `/subdepartment/${subDepWithFamilies?.id}/full`
    }
    if (sameSubdep) {
      return `/subdepartment/${subDepWithFamilies?.id}`
    }
    return '/subdepartments'
  }

  const goToProductPage = async () => {
    trackAnalyticsEvent(ANALYTICS_EVENTS.NAV_FAMILIES_SUBMITTED, {
      season,
      journey_channel: ANALYTICS_JOURNEY_CHANNELS.HIERARCHY_NAVIGATION,
      universe_id: subDepWithFamilies?.universe.id,
      department_id: subDepWithFamilies?.department.id,
      subdepartment_id: subDepWithFamilies?.id,
      families_selected_count: selectedFamilies.length,
    })

    const urlPath = `/${season}/universe/${subDepWithFamilies?.universe.id}/department/${subDepWithFamilies?.department.id}${getSubdepPath()}/families`
    const searchParams = new URLSearchParams()

    for (const family of selectedFamilies) {
      searchParams.append('families', family.id.toString())
    }

    const fullUrlPath = `${urlPath}?${searchParams.toString()}`
    await navigate(fullUrlPath)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Tab' && e.shiftKey) {
      const lastFocusableElement = document.querySelector('#select-all-checkbox')
      if (e.target === lastFocusableElement) {
        const selectedItem = document.querySelector('.subdepartment-label.selected') as HTMLElement

        if (selectedItem) {
          e.preventDefault()
          selectedItem.focus()
        }
      }
    }
  }

  return (
    <section
      className="families-container"
      data-testid="families-container"
      aria-label={t('families')}
      ref={innerRef}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      {isLoading
        ? (
            <div className="loader-container" data-testid="loader-container">
              <VpLoader />
            </div>
          )
        : (
            <>
              <FamiliesModal
                families={selectedFamilies}
                openModal={openModal}
                validate={setSelectedFamilies}
                setOpenModal={setOpenModal}
              />
              <div className="families-header" data-testid="families_header">
                <div className="families-count" data-testid="families_count">
                  {familiesCount > 0
                    ? t('families-count', { count: familiesCount })
                    : t('families')}
                </div>
                <div
                  className="select-all-families"
                  data-testid="select-all-families"
                >
                  <VpCheckbox
                    id="select-all-checkbox"
                    key="select-all-checkbox"
                    checked={checked}
                    data-testid="select-all_checkbox"
                    onChange={event => event && toggleAllFamilies()}
                    disabled={familiesCount === 0}
                  >
                    {t('select-all-families')}
                  </VpCheckbox>
                </div>
              </div>
              {!subDepWithFamilies || subDepWithFamilies.families.length === 0
                ? (
                    <div
                      className="families-empty-content"
                      data-testid="families-empty-content"
                    >
                      {t('empty-family-block')}
                    </div>
                  )
                : (
                    <div className="families-content" data-testid="families-content">
                      {subDepWithFamilies.families.map(family => (
                        <FamilyCard
                          key={family.id}
                          family={family}
                          selectCard={() =>
                            toggleFamily({
                              ...family,
                              subdepartmentId: subDepWithFamilies.id,
                              subdepartmentLabels: subDepWithFamilies.labels,
                            })}
                          isSelected={selectedFamilies
                            .flatMap(family => family.id)
                            .includes(family.id)}
                        />
                      ))}
                    </div>
                  )}
            </>
          )}
      {selectedFamilies.length > 0 && (
        <FamiliesFooter
          families={selectedFamilies}
          toggleFamily={toggleFamily}
          setOpenModal={setOpenModal}
          goToProductPage={goToProductPage}
        />
      )}
    </section>
  )
}
